import React, { useState } from "react";
import {
  useEmailCycleDetailsStore,
  useRemoveEmailCycleRecipientStore,
} from "../../stores/Hooks";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  LoadingIndicator,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { useRouteNavigator } from "../../../../routes/RoutesHelper";
import { RemoveEmailCycleRecipientRPC } from "@pulse/pulse-rpcs/lib/email-cycle/removeEmailCycleRecipientRPC";
import { Stack, Typography } from "@mui/material";
import { HelpCircle } from "lucide-react";
import { observer } from "mobx-react";
import { ErrorDialog } from "../../../../components/dialogs/ErrorDialog";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { SuccessDialog } from "../../../../components/dialogs/SuccessDialog";
import { EMPTY_CHARACTER } from "@pulse/shared-components";

interface ConfirmRemoveEmailCycleRecipientDialogProps {
  scheduleId: string;
  handleDialogClose: VoidFunction;
}

type DialogToOpen =
  | "CONFIRM_REMOVE_RECIPIENT"
  | "SUCCESS"
  | "SCHEDULE_IN_PROGRESS_ERROR"
  | "UNEXPECTED_ERROR"
  | undefined;

const CONFIRM_STOP_DIALOG_DESCRIPTION_ICON = 20;
const CONFIRM_STOP_DIALOG_WIDTH = "560px";

export const ConfirmRemoveEmailCycleRecipientDialog: React.FC<ConfirmRemoveEmailCycleRecipientDialogProps> =
  observer(({ scheduleId, handleDialogClose }) => {
    const [dialogToOpen, setDialogToOpen] = useState<DialogToOpen>(
      "CONFIRM_REMOVE_RECIPIENT",
    );

    const { t } = useTranslation();
    const spacing = useSpacing();
    const typography = useTypography();
    const tokens = useFoundationColorTokens();
    const navigateTo = useRouteNavigator();

    const emailCycleDetailsStore = useEmailCycleDetailsStore();
    const removeEmailCycleRecipientStore = useRemoveEmailCycleRecipientStore();

    const handleRemoveRecipient = async (
      scope: RemoveEmailCycleRecipientRPC.RequestEnums.Scope.Scope,
    ): Promise<void> => {
      setDialogToOpen(undefined);
      try {
        await removeEmailCycleRecipientStore.removeEmailCycleRecipient(
          scheduleId,
          scope,
        );

        switch (
          removeEmailCycleRecipientStore.removeEmailCycleRecipientRPCError
        ) {
          case null:
            setDialogToOpen("SUCCESS");
            break;
          case RemoveEmailCycleRecipientRPC.RPCError.InvalidScheduleId:
          case RemoveEmailCycleRecipientRPC.RPCError.InvalidEmailId:
            navigateTo.pageNotFound();
            return;
          case RemoveEmailCycleRecipientRPC.RPCError.ScheduleInProgress:
            setDialogToOpen("SCHEDULE_IN_PROGRESS_ERROR");
            break;
          default:
            removeEmailCycleRecipientStore.removeEmailCycleRecipientRPCError satisfies never;
        }
      } catch {
        setDialogToOpen("UNEXPECTED_ERROR");
      }
    };

    return (
      <>
        <Dialog
          open={dialogToOpen === "CONFIRM_REMOVE_RECIPIENT"}
          title={t("emailCycles.removeRecipientDialogTitle")}
          primaryButtonText={t("emailCycles.removeRecipientThisCycleLabel")}
          primaryButtonColor="destructive"
          onPrimaryButtonClick={() =>
            handleRemoveRecipient(
              RemoveEmailCycleRecipientRPC.RequestEnums.Scope.Scope.ONLY_THIS,
            )
          }
          secondaryButtonText={
            emailCycleDetailsStore.scheduleType.type === "RECURRING_SCHEDULE"
              ? t("emailCycles.removeRecipientThisAndPendingCyclesLabel")
              : undefined
          }
          onSecondaryButtonClick={() =>
            handleRemoveRecipient(
              RemoveEmailCycleRecipientRPC.RequestEnums.Scope.Scope
                .THIS_AND_PENDING,
            )
          }
          isSecondaryButtonDisabled={
            removeEmailCycleRecipientStore.loadingRemoveEmailCycleRecipient
          }
          onClose={removeEmailCycleRecipientStore.resetStore}
          width={CONFIRM_STOP_DIALOG_WIDTH}
          showCloseButton
          disableBackdropClick
        >
          <Stack gap={spacing.spaceLG}>
            <Stack
              direction="row"
              color={tokens.labelError}
              gap={spacing.spaceXS}
            >
              <HelpCircle
                size={CONFIRM_STOP_DIALOG_DESCRIPTION_ICON}
                style={{ flexShrink: 0 }}
              />
              <Typography {...typography.b2}>
                {t("emailCycles.removeRecipientCycleDialogDescription", {
                  email:
                    removeEmailCycleRecipientStore.recipientDetails?.email ??
                    EMPTY_CHARACTER,
                })}
              </Typography>
            </Stack>
          </Stack>
        </Dialog>

        <LoadingIndicator
          isLoading={
            removeEmailCycleRecipientStore.loadingRemoveEmailCycleRecipient
          }
          variant="overlay"
        />

        {dialogToOpen === "SUCCESS" && (
          <SuccessDialog
            title={t("emailCycles.removeRecipientSuccessDialogTitle")}
            message={t("emailCycles.removeRecipientSuccessDialogDescription")}
            onClickClose={handleDialogClose}
          />
        )}

        {dialogToOpen === "SCHEDULE_IN_PROGRESS_ERROR" && (
          <ErrorDialog
            title={t("emailCycles.removeRecipientFailed")}
            message={t(
              "emailCycles.removeRecipientScheduleInProgressErrorMessage",
            )}
            onClickClose={handleDialogClose}
          />
        )}

        {dialogToOpen === "UNEXPECTED_ERROR" && (
          <UnexpectedErrorDialog
            message={t("emailCycles.removeRecipientUnexpectedErrorMessage")}
            onClickDone={handleDialogClose}
          />
        )}
      </>
    );
  });
